import { FullTxnResponse } from 'app/modules/transactions/types/responses';
import { TRANSACTION_QUERY_KEYS } from 'app/modules/transactions/queries/keys';
import { getWithParsedError, post } from 'app/shared/utils/fetchr';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectDLEnabledForTxnEventDetails } from 'app/shared/featureFlags/selectors';
import { CGDOLoadingResponse } from 'app/modules/investigations/types/responses';
import {
  getLoadingFromColdStorageStateForCGDO,
  getColdStorageRetryWaitTime,
  COLD_STORAGE_MAX_RETRIES,
  isCGDOLoadingResponse,
} from 'app/shared/utils/fastArchHelpers';
import { useCGDOProgressiveData } from 'app/shared/hooks/useCGDOProgressiveData';
import { useRef, useMemo, useEffect } from 'react';

type TransactionResponse =
  | FullTxnResponse
  | (FullTxnResponse & CGDOLoadingResponse)
  | CGDOLoadingResponse;

export const useGetTransactionById = <TSelect = FullTxnResponse>(
  id?: string,
  select: (data: FullTxnResponse) => TSelect = (data) => data as TSelect,
) => {
  const useNewEndpoint = useSelector(selectDLEnabledForTxnEventDetails);
  const dataUpdateCountRef = useRef(0);

  const query = useQuery<TransactionResponse, Error, TransactionResponse>({
    queryFn: async () => {
      if (useNewEndpoint) {
        return getWithParsedError<TransactionResponse>(
          `/v1/txn-events/cached/${encodeURIComponent(id!)}`,
        );
      }
      return post<FullTxnResponse>(`/v1/txn-events/get`, {
        txn_event_id: `${id}`,
      });
    },
    queryKey: TRANSACTION_QUERY_KEYS.getTransactionById(id, useNewEndpoint),
    enabled: !!id,
    staleTime: 10000,
    refetchInterval: ({ state: { data } }) => {
      if (useNewEndpoint) {
        if (
          data &&
          isCGDOLoadingResponse(data) &&
          dataUpdateCountRef.current < COLD_STORAGE_MAX_RETRIES
        ) {
          return getColdStorageRetryWaitTime(dataUpdateCountRef.current);
        }
      }
      // Do not retry by default
      return false;
    },
  });

  const coldStorageLoading = useMemo(
    () =>
      id
        ? getLoadingFromColdStorageStateForCGDO(
            id,
            query.data && isCGDOLoadingResponse(query.data)
              ? query.data.data_loader
              : undefined,
          )
        : undefined,
    [id, query.data],
  );

  useEffect(() => {
    // Reset update count when the ID changes
    dataUpdateCountRef.current = 0;
  }, [id]);

  // Increase the countRef whenever we get a loading response
  useEffect(() => {
    if (query.data && isCGDOLoadingResponse(query.data)) {
      dataUpdateCountRef.current += 1;
    }
  }, [query.data]);

  const { data, isLoading } = useCGDOProgressiveData(
    query.data,
    query.isLoading,
    query.isError,
    select,
  );

  return {
    // This is still accesible if needed for whatever reason
    query,
    // The following values already take into account the cold storage loading state
    data,
    isLoading,
    coldStorageLoading,
  };
};
